import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TimeSeriesApiService } from './time-series.service';
import {
  loadTimeseriesInfoApis,
  loadTimeseriesInfoApisSuccess,
  loadTimeseriesInfoApisFailure,
} from './time-series.actions';
import { catchError, forkJoin, map, mergeMap, Observable, of } from 'rxjs';
import { AppModule } from '@app/enums';

const safeApi = <T>(source$: Observable<T>, key: string) =>
  source$.pipe(
    map((data) => ({ success: true, data })),
    catchError((error) => {
      console.error(`${key} API failed`, error);
      return of({ success: false, error });
    })
  );

@Injectable()
export class TimeseriesApiEffects {
  private actions$ = inject(Actions);
  private tsService = inject(TimeSeriesApiService);

  private TS_CONFIG: any = {
    [AppModule.ETF]: {
      instrument: () => this.tsService.fetchInstrumentTSInfo(),
      portfolio: () => this.tsService.fetchPortfolioTSInfo(),
    },
    [AppModule.INSURANCE]: {
      liabilities: () => this.tsService.fetchLiabilitiesTSInfo(),
      assets: () => this.tsService.fetchAssetTSInfo(),
      instrument: () => this.tsService.fetchInstrumentTSInfo(),
    },
    [AppModule.OSMA]: {
      account: () => this.tsService.fetchOSMAAccountTSInfo(),
      advisor: () => this.tsService.fetchOSMAAdvisorTSInfo(),
      accountAsset: () => this.tsService.fetchOSMAAccountAssetTSInfo(),
      advisorAsset: () => this.tsService.fetchOSMAAdvisorAssetTSInfo(),
    },
    // ocio
    //osma
  };

  loadTimeseriesInfoApis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTimeseriesInfoApis),
      mergeMap(({ module }) => {
        const config = this.TS_CONFIG[module] || {};
        const tsCalls: any = {};

        Object.entries(config).forEach(([page, apiFn]: any) => {
          const key = `${page}TsInfo`;
          tsCalls[key] = safeApi(apiFn(), key);
        });
        if (Object.keys(tsCalls).length === 0) {
          return of(loadTimeseriesInfoApisSuccess({ data: {} }));
        }

        return forkJoin(tsCalls).pipe(
          map((responses: any) => {
            const result: any = {};
            const failures: string[] = [];

            const mapByKey = (arr: any[], key: string) =>
              arr.reduce((acc, item) => {
                acc[item[key]] = item;
                return acc;
              }, {});

            const mappings: any = {
              instrumentTsInfo: 'ts_id',
              portfolioTsInfo: 'ts_id',
              liabilitiesTsInfo: 'ts_id',
              assetTsInfo: 'ts_id',
              advisorTsInfo: 'ts_id',
              accountsTsInfo: 'ts_id',
              advisorAssetTsInfo: 'ts_id',
              accountAssetTsInfo: 'ts_id',
              accountPositionsTsInfo: 'ts_id'
            };

            for (const [key, resp] of Object.entries(responses)) {
              const res: any = resp;
              if (res.success) {
                result[key] = res.data;

                if (mappings[key] && res.data?.data) {
                  result[key].mapedById = mapByKey(res.data.data, mappings[key]);
                }
              } else {
                failures.push(key);
                result[key] = null;
              }
            }

            return loadTimeseriesInfoApisSuccess({ data: result });
          }),
          catchError((error) => of(loadTimeseriesInfoApisFailure({ error })))
        );
      })
    )
  );
}
